import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Card, EmptyState, SectionTitle, Money } from "@/components/dash";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Users, Megaphone, Loader2, Send } from "lucide-react";
import { toast } from "sonner";

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data)).finally(() => setLoading(false));
    api.get("/marketing/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const reachable = stats ? Math.max(0, (stats.past_customers || 0) - (stats.opted_out || 0)) : null;

  return (
    <div className="space-y-6">
      <SectionTitle>Customers</SectionTitle>

      <AnnounceComposer reachable={reachable} />

      {loading ? (
        <div className="h-64 animate-pulse rounded-2xl bg-cocoa/5" />
      ) : customers.length === 0 ? (
        <EmptyState icon={Users} title="No customers yet" body="Your customer list is built automatically from placed orders. Share your storefront link to start collecting preorders." />
      ) : (
        <Card className="overflow-x-auto p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-cocoa/10 hover:bg-transparent">
                <TableHead className="text-cocoa/60">Name</TableHead>
                <TableHead className="text-cocoa/60">Contact</TableHead>
                <TableHead className="text-cocoa/60">Orders</TableHead>
                <TableHead className="text-cocoa/60">Total value</TableHead>
                <TableHead className="text-cocoa/60">Last order</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {customers.map((c, i) => (
                <TableRow key={i} className="border-cocoa/5" data-testid={`customer-row-${i}`}>
                  <TableCell className="font-medium text-cocoa">{c.name}</TableCell>
                  <TableCell className="text-sm text-cocoa/60">
                    <div>{c.email}</div>
                    {c.phone && <div className="text-xs">{c.phone}</div>}
                  </TableCell>
                  <TableCell className="text-cocoa">{c.order_count}</TableCell>
                  <TableCell className="text-cocoa"><Money value={c.total_value} /></TableCell>
                  <TableCell className="text-sm text-cocoa/60">{new Date(c.last_order).toLocaleDateString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}

function AnnounceComposer({ reachable }) {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!subject.trim()) return toast.error("Add a subject line");
    if (!message.trim()) return toast.error("Write a short message");
    const count = reachable != null ? ` to ${reachable} customer${reachable !== 1 ? "s" : ""}` : "";
    if (!window.confirm(`Send this announcement${count}?`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/marketing/announce", { subject: subject.trim(), message: message.trim() });
      if (data.sent > 0) toast.success(data.message || "Announcement sent");
      else if (data.failed > 0) toast.error(data.message || "We couldn't deliver the announcement.");
      else toast(data.message || "No customers to notify yet.");
      setSubject(""); setMessage("");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Couldn't send the announcement"); }
    finally { setSending(false); }
  };

  return (
    <Card data-testid="announce-composer">
      <div className="mb-3 flex items-center gap-2">
        <Megaphone className="h-5 w-5 text-terracotta" strokeWidth={1.6} />
        <div>
          <h2 className="font-serif text-xl font-semibold text-cocoa">Send an announcement</h2>
          <p className="text-sm text-cocoa/60">Email all your past customers{reachable != null ? ` (${reachable} reachable)` : ""}. Unsubscribed customers are skipped automatically.</p>
        </div>
      </div>
      <div className="space-y-3">
        <div>
          <Label className="text-cocoa">Subject</Label>
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. Holiday preorders are open!" className="mt-1.5 rounded-xl bg-cream" data-testid="announce-subject" />
        </div>
        <div>
          <Label className="text-cocoa">Message</Label>
          <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} placeholder="Share your news, a new drop, seasonal specials..." className="mt-1.5 rounded-xl bg-cream" data-testid="announce-message" />
        </div>
        <Button onClick={send} disabled={sending} className="rounded-full bg-terracotta text-warmwhite hover:bg-terracotta/90" data-testid="announce-send">
          {sending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Send className="mr-1.5 h-4 w-4" />} Send to customers
        </Button>
      </div>
    </Card>
  );
}
